import React, { useState, useEffect, useCallback } from 'react';
import { getAccountHistory } from '../services/api';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const TransactionHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: 'all', from: '', to: '' });

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAccountHistory();
      if (response.data.success) {
        let filtered = response.data.history;
        if (filter.type !== 'all') {
          filtered = filtered.filter(h => h.type === filter.type);
        }
        if (filter.from) {
          filtered = filtered.filter(h => new Date(h.created_at) >= new Date(filter.from));
        }
        if (filter.to) {
          filtered = filtered.filter(h => new Date(h.created_at) <= new Date(filter.to));
        }
        setHistory(filtered);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const exportCSV = async () => {
    window.open('http://localhost:5000/api/account/export?format=csv', '_blank');
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'deposit': return 'text-emerald-500';
      case 'withdrawal': return 'text-red-500';
      case 'trade_pnl': return 'text-blue-500';
      default: return 'text-gray-400';
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'credited' || status === 'sent' || status === 'completed') return 'badge-success';
    if (status === 'pending') return 'badge-warning';
    return 'badge-info';
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-xl font-bold text-white">Transaction History</h1>
        <div className="flex gap-2">
          <button onClick={exportCSV} className="btn-secondary btn-sm flex items-center gap-1">
            <ArrowDownTrayIcon className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>
      
      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Type</label>
            <select 
              value={filter.type}
              onChange={(e) => setFilter({...filter, type: e.target.value})}
              className="input-dark text-sm py-1.5"
            >
              <option value="all">All</option>
              <option value="deposit">Deposits</option>
              <option value="withdrawal">Withdrawals</option>
              <option value="trade_pnl">Trade P&L</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">From</label>
            <input 
              type="date" 
              value={filter.from}
              onChange={(e) => setFilter({...filter, from: e.target.value})}
              className="input-dark text-sm py-1.5"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">To</label>
            <input 
              type="date" 
              value={filter.to}
              onChange={(e) => setFilter({...filter, to: e.target.value})}
              className="input-dark text-sm py-1.5"
            />
          </div>
          <button 
            onClick={() => setFilter({ type: 'all', from: '', to: '' })}
            className="btn-secondary btn-sm"
          >
            Reset
          </button>
        </div>
      </div>
      
      {/* History Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table-dark w-full">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Reference</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="text-center py-8 text-gray-400">Loading transactions...</td></tr>
              ) : history.length === 0 ? (
                <tr><td colSpan="5" className="text-center py-8 text-gray-500">No transactions found</td></tr>
              ) : (
                history.map((item, idx) => (
                  <tr key={idx}>
                    <td className="text-sm text-gray-400">{new Date(item.created_at).toLocaleString()}</td>
                    <td>
                      <span className={`text-xs font-medium ${getTypeColor(item.type)}`}>
                        {item.type === 'trade_pnl' ? 'TRADE P&L' : item.type.toUpperCase()}
                      </span>
                    </td>
                    <td className={`font-mono ${parseFloat(item.amount) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {parseFloat(item.amount) >= 0 ? '+' : ''}{parseFloat(item.amount).toFixed(2)} USDT
                    </td>
                    <td>
                      <span className={getStatusBadge(item.status)}>
                        {item.status || 'Completed'}
                      </span>
                    </td>
                    <td className="font-mono text-xs text-gray-500">{item.reference?.slice(0, 16)}...</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;